import { useEffect, useMemo, useState } from "react";
import { BarChart3, ChevronDown, ChevronUp } from "lucide-react";
import type { CaseSummary } from "@/lib/api";
import {
  computeVerificationReasons,
  type ReasonWindow,
} from "@/lib/verificationReasons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STORAGE_KEY = "dv-verification-reasons-open-v1";

function readOpen(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeOpen(open: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, open ? "true" : "false");
  } catch {
    /* ignore */
  }
}

export function VerificationReasons({
  cases,
  activeReasonKey,
  onSelectReason,
}: {
  cases: CaseSummary[];
  activeReasonKey?: string | null;
  onSelectReason?: (key: string, label: string) => void;
}) {
  const [open, setOpen] = useState<boolean>(false);
  const [window, setWindow] = useState<ReasonWindow>(30);

  // Collapsed-row summary always uses the 30d window per the mock.
  const summary30 = useMemo(
    () => computeVerificationReasons(cases, 30),
    [cases],
  );
  const active = useMemo(
    () => computeVerificationReasons(cases, window),
    [cases, window],
  );

  if (cases.length === 0) return null;

  const max = Math.max(...active.rows.map((r) => r.count), 1);

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="verification-reasons-body"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600">
            <BarChart3 className="h-3.5 w-3.5" />
          </div>
          <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
            <h2 className="text-sm font-semibold text-slate-950 leading-none">
              Verification Insights
            </h2>
            {!open && (
              <span className="text-xs text-slate-500 tabular-nums truncate">
                Last 30 days
                {summary30.topLabel ? ` · Top reason: ${summary30.topLabel}` : ""}
                {` · ${summary30.total} total ${summary30.total === 1 ? "request" : "requests"}`}
                {` · ${summary30.outcomes.verified} Verified`}
              </span>
            )}
          </div>
        </div>
        <span className="shrink-0 flex items-center gap-1 text-xs text-slate-500">
          {open ? "Hide" : "Show"}
          {open ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
        </span>
      </button>

      {open && (
        <div
          id="verification-reasons-body"
          className="px-5 sm:px-6 pb-5 border-t border-border pt-4"
        >
          {/* Chart */}
          <div>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h3 className="text-sm font-semibold text-foreground">
                Verification reasons
              </h3>
              <Select
                value={String(window)}
                onValueChange={(v) =>
                  setWindow(v === "all" ? "all" : (Number(v) as ReasonWindow))
                }
              >
                <SelectTrigger className="h-8 w-[140px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="all">All time</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {active.total === 0 ? (
              <p className="text-sm text-muted-foreground py-6">
                No verification requests in this window.
              </p>
            ) : (
              <div className="space-y-1">
                {active.rows.map((r) => {
                  const isActive = activeReasonKey === r.key;
                  const clickable = !!onSelectReason && r.count > 0;
                  return (
                    <button
                      type="button"
                      key={r.key}
                      disabled={!clickable}
                      onClick={() => onSelectReason?.(r.key, r.label)}
                      aria-pressed={isActive}
                      className={`w-full grid grid-cols-[180px_1fr_72px] items-center gap-3 rounded-md px-2 py-1.5 text-left transition-colors ${
                        clickable ? "hover:bg-violet-50 cursor-pointer" : "cursor-default"
                      } ${isActive ? "bg-violet-50 ring-1 ring-violet-300" : ""}`}
                    >
                      <span className="text-xs text-foreground truncate" title={r.label}>
                        {r.label}
                      </span>
                      <div className="relative h-2.5 rounded-full bg-muted overflow-hidden">
                        <div
                          className="absolute inset-y-0 left-0 bg-violet-600 rounded-full transition-all"
                          style={{ width: `${(r.count / max) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs tabular-nums text-muted-foreground shrink-0 text-right">
                        <span className="font-semibold text-foreground">{r.count}</span>{" "}
                        ({r.percent}%)
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Review Outcomes — compact secondary strip */}
          <ReviewOutcomesRow outcomes={active.outcomes} />
        </div>
      )}
    </section>
  );
}

function pct(n: number, total: number): string {
  if (total <= 0) return "—";
  return `${Math.round((n / total) * 100)}%`;
}

function ReviewOutcomesRow({
  outcomes,
}: {
  outcomes: import("@/lib/verificationReasons").ReviewOutcomes;
}) {
  const { verified, not_verified, pending_review, still_open, total } = outcomes;
  const items: { label: string; count: number; dot: string; text: string; tip: string }[] = [
    { label: "Verified", count: verified, dot: "bg-emerald-500", text: "text-emerald-700", tip: "Cases marked Verified in the selected timeframe." },
    { label: "Not Verified", count: not_verified, dot: "bg-pink-500", text: "text-pink-700", tip: "Cases marked Not Verified in the selected timeframe." },
    { label: "Pending Review", count: pending_review, dot: "bg-violet-500", text: "text-violet-700", tip: "Cases currently in Needs Review in the selected timeframe." },
    { label: "Not Ready for Review", count: still_open, dot: "bg-sky-500", text: "text-sky-700", tip: "Active cases not yet ready for final review — includes New, In Progress, Waiting on Applicant, and Waiting on Vouch. Excludes dropped-off / closed cases." },
  ];

  return (
    <div className="mt-5 pt-4 border-t border-slate-200">
      <h3 className="text-sm font-semibold text-slate-900 mb-2">Outcomes in this period</h3>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600">
        {items.map((it, i) => (
          <div key={it.label} className="flex items-center gap-1.5" title={it.tip}>
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${it.dot}`} />
            <span className={`font-medium ${it.text}`}>{it.label}</span>
            <span className="tabular-nums text-slate-700">
              {it.count}
              {total > 0 ? (
                <span className="text-slate-500"> ({pct(it.count, total)})</span>
              ) : (
                <span className="text-slate-500"> (—)</span>
              )}
            </span>
            {i < items.length - 1 && (
              <span className="text-slate-300 ml-2" aria-hidden>
                ·
              </span>
            )}
          </div>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-slate-400">Based on the selected time period.</p>
    </div>
  );
}
